import { useRef, useCallback, useEffect, useState } from 'react'

export interface UseInfiniteScrollOptions {
  /** Ref to the scrollable container */
  scrollRef: React.RefObject<HTMLElement | null>
  /** Called when more items should be loaded */
  onLoadMore: () => void | Promise<void>
  /** Whether there are more items to load */
  hasMore: boolean
  /** Whether currently loading */
  isLoading: boolean
  /** Start loading when this fraction of scroll remains (0-1). Default: 0.2 (80% scrolled) */
  threshold?: number
  /** Enable pull-up rubber-band effect at the bottom */
  rubberBand?: boolean
}

export interface UseInfiniteScrollReturn {
  /** Current rubber-band pull distance (px, 0 when not pulling) */
  pullDistance: number
  /** Whether the user is actively pulling */
  isPulling: boolean
}

const RUBBER_BAND_RESISTANCE = 0.35
const MAX_PULL = 80

export function useInfiniteScroll({
  scrollRef,
  onLoadMore,
  hasMore,
  isLoading,
  threshold = 0.2,
  rubberBand = true,
}: UseInfiniteScrollOptions): UseInfiniteScrollReturn {
  const [pullDistance, setPullDistance] = useState(0)
  const [isPulling, setIsPulling] = useState(false)
  const touchStartY = useRef(0)
  const isAtBottom = useRef(false)
  const loadingTriggered = useRef(false)

  // Reset loading trigger when loading completes
  useEffect(() => {
    if (!isLoading) {
      loadingTriggered.current = false
    }
  }, [isLoading])

  // Scroll-based preloading
  const handleScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el || isLoading || !hasMore || loadingTriggered.current) return

    const { scrollTop, scrollHeight, clientHeight } = el
    const remainingScroll = scrollHeight - scrollTop - clientHeight
    const thresholdPx = clientHeight * threshold

    if (remainingScroll <= thresholdPx) {
      loadingTriggered.current = true
      onLoadMore()
    }
  }, [scrollRef, onLoadMore, hasMore, isLoading, threshold])

  // Rubber-band touch handlers
  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (!rubberBand) return
    const el = scrollRef.current
    if (!el) return

    touchStartY.current = e.touches[0].clientY
    const { scrollTop, scrollHeight, clientHeight } = el
    isAtBottom.current = scrollTop + clientHeight >= scrollHeight - 1
  }, [scrollRef, rubberBand])

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!rubberBand || !isAtBottom.current) return
    const el = scrollRef.current
    if (!el) return

    // Re-check if at bottom (might have scrolled up)
    const { scrollTop, scrollHeight, clientHeight } = el
    if (scrollTop + clientHeight < scrollHeight - 1) {
      isAtBottom.current = false
      setPullDistance(0)
      setIsPulling(false)
      return
    }

    const deltaY = touchStartY.current - e.touches[0].clientY
    if (deltaY <= 0) {
      // Pulling up (scrolling down past end) — ignore, we want pull-down-past-end which is deltaY > 0
      // Actually: user swipes UP (finger moves up) to "pull" more content from below
      // That means clientY decreases, so deltaY = startY - currentY > 0
    }

    if (deltaY > 0) {
      // User is pulling up past the bottom
      const dampened = Math.min(deltaY * RUBBER_BAND_RESISTANCE, MAX_PULL)
      setPullDistance(dampened)
      setIsPulling(true)
    } else {
      setPullDistance(0)
      setIsPulling(false)
    }
  }, [scrollRef, rubberBand])

  const handleTouchEnd = useCallback(() => {
    if (!rubberBand) return
    if (isPulling && pullDistance > 20 && hasMore && !isLoading && !loadingTriggered.current) {
      loadingTriggered.current = true
      onLoadMore()
    }
    setPullDistance(0)
    setIsPulling(false)
    isAtBottom.current = false
  }, [rubberBand, isPulling, pullDistance, hasMore, isLoading, onLoadMore])

  // Attach listeners
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    el.addEventListener('scroll', handleScroll, { passive: true })
    el.addEventListener('touchstart', handleTouchStart, { passive: true })
    el.addEventListener('touchmove', handleTouchMove, { passive: true })
    el.addEventListener('touchend', handleTouchEnd)

    return () => {
      el.removeEventListener('scroll', handleScroll)
      el.removeEventListener('touchstart', handleTouchStart)
      el.removeEventListener('touchmove', handleTouchMove)
      el.removeEventListener('touchend', handleTouchEnd)
    }
  }, [scrollRef, handleScroll, handleTouchStart, handleTouchMove, handleTouchEnd])

  return { pullDistance, isPulling }
}
